import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { post } from 'api';
import { enqueueSnackbar } from 'notistack';
import * as yup from 'yup';

const enquirySchema = yup.object({
  documentType: yup.string().required('Type Of Document is required'),

  otherDocumentName: yup.string().when('documentType', {
    is: 'Other',
    then: (s) => s.trim().required('Document Name is required').max(30, 'Max 30 chars'),
    otherwise: (s) => s.optional().nullable(),
  }),

  // this is the final value you'll send
  documentName: yup.string().required('Document Name is required'),

  file: yup
    .mixed()
    .test('fileSize', 'File is too large (max 5MB)', (value) => {
      if (!value) return true;
      if (typeof value === 'string') return true;
      if (value instanceof File) return value.size <= 5 * 1024 * 1024;
      return true;
    })
    .required('Document is required'),
});

export default function AddDocument({
  open,
  setOpen,
  fetchData,
  customerId,
  document = null,
  closeAll,
}) {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: yupResolver(enquirySchema),
    defaultValues: {
      documentType: '',
      otherDocumentName: '',
      documentName: '',
      file: null,
    },
  });

  const [preview, setPreview] = useState({ file: null });

  const documentType = watch('documentType');
  const otherDocumentName = watch('otherDocumentName');

  const EMPTY_FORM = {
    documentType: '',
    otherDocumentName: '',
    documentName: '',
    file: null,
  };

  useEffect(() => {
    if (document) {
      const isNotOther = [
        'AADHAR',
        'PAN',
        'ELECTRICITY_BILL',
        'CANCEL_CHEQUE',
        'INDEX_COPY',
        'MUNICIPALITY_TAX',
      ].includes(document.name);
      const obj = {
        ...EMPTY_FORM,
        documentType: isNotOther ? document.name : 'Other',
        documentName: document.name,
        otherDocumentName: isNotOther ? '' : document.name,
      };
      reset(obj, { keepDefaultValues: true });
    } else {
      reset(EMPTY_FORM, { keepDefaultValues: true });
    }
  }, [document]);

  // ✅ Keep documentName in sync
  useEffect(() => {
    if (!documentType) {
      setValue('documentName', '');
      return;
    }

    if (documentType === 'Other') {
      setValue('documentName', (otherDocumentName || '').trim());
    } else {
      setValue('documentName', documentType); // AADHAR/PAN/... becomes documentName
      setValue('otherDocumentName', '');
    }
  }, [documentType, otherDocumentName, setValue]);

  const onClose = () => {
    reset();
    setPreview({ file: null });
    setOpen(false);
    fetchData();
  };

  const submitStock = async (data) => {
    try {
      const formData = new FormData();
      let res;
      if (document) {
        formData.append('id', document._id);
        if (data.file) formData.append('file', data.file);
        res = await post('users/update_document_file', formData);
      } else {
        formData.append('documentName', data.documentName);
        formData.append('file', data.file);
        formData.append('customerId', customerId);
        res = await post('users/add_document', formData);
      }

      if (res.status == 200) {
        enqueueSnackbar(res.message || 'Customer Created Successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      } else {
        enqueueSnackbar(res.message || 'Error While Creating Customer', {
          variant: 'error',
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar(error.message || 'Error While Creating Customer Try Again Later', {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      closeAll();
      reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      component="form"
      onSubmit={handleSubmit(submitStock)}
    >
      {/* HEADER */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 1 }}>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Add Document</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* BODY */}
      <DialogContent sx={{ px: 2 }}>
        <Grid container spacing={2}>
          {/* ✅ Select: documentType */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="documentType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth error={!!errors.documentType}>
                  <InputLabel>Type Of Document</InputLabel>
                  <Select {...field} label="Type Of Document" disabled={document}>
                    {[
                      { _id: 'AADHAR', name: 'AADHAR' },
                      { _id: 'PAN', name: 'PAN' },
                      { _id: 'ELECTRICITY_BILL', name: 'ELECTRICITY_BILL' },
                      { _id: 'CANCEL_CHEQUE', name: 'CANCEL_CHEQUE' },
                      { _id: 'MUNICIPALITY_TAX', name: 'MUNICIPALITY_TAX' },
                      { _id: 'INDEX_COPY', name: 'INDEX_COPY' },
                      { _id: 'Other', name: 'Other' },
                    ].map((c) => (
                      <MenuItem key={c._id} value={c._id}>
                        {c.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{errors.documentType?.message}</FormHelperText>
                </FormControl>
              )}
            />
          </Grid>
          {documentType == 'Other' && documentType == '' && (
            <Grid item size={{ xs: 12, sm: 6 }}></Grid>
          )}

          {/* ✅ Show input ONLY when Other */}
          {documentType === 'Other' && (
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="otherDocumentName"
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Document Name"
                    error={!!errors.otherDocumentName}
                    helperText={errors.otherDocumentName?.message}
                    slotProps={{ htmlInput: { maxLength: 30 } }}
                  />
                )}
              />
            </Grid>
          )}

          {/* (Optional) show final computed documentName read-only when not Other */}
          {documentType && documentType !== 'Other' && (
            <Grid item size={{ xs: 12, sm: 6 }}>
              <Controller
                control={control}
                name="documentName"
                render={({ field }) => (
                  <TextField {...field} fullWidth label="Document Name" disabled />
                )}
              />
            </Grid>
          )}

          {/* ✅ File */}
          <Grid item size={{ xs: 12, sm: 6 }}>
            <Controller
              name="file"
              control={control}
              render={({ field }) => (
                <>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    sx={{ textTransform: 'none' }}
                  >
                    {preview?.file?.name || 'Upload Document'}
                    <input
                      type="file"
                      hidden
                      accept="image/*,application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        field.onChange(file); // ✅ store File, not FileList
                        setPreview({ file });
                      }}
                    />
                  </Button>

                  <Typography color="error" variant="caption">
                    {errors.file?.message}
                  </Typography>
                </>
              )}
            />
          </Grid>

          {/* Preview */}
          {/* <Grid item size={{ xs: 12, sm: 3 }}>
            {preview?.file && (
              <Box>
                <img
                  src={URL.createObjectURL(preview.file)}
                  alt="Preview"
                  width="50%"
                  style={{ borderRadius: 8, cursor: 'pointer' }}
                />
              </Box>
            )}
          </Grid> */}
          <Grid item size={{ xs: 12, sm: 1 }}>
            {preview?.file && (
              <Box>
                {preview.file.type == 'application/pdf' ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                    onClick={() => window.open(URL.createObjectURL(preview?.file))}
                  >
                    <PictureAsPdfIcon color="error" sx={{ fontSize: 40 }} />
                    {/* <Typography variant="caption">{preview?.degreeCertificate?.name}</Typography> */}
                  </Box>
                ) : (
                  <img
                    src={URL.createObjectURL(preview?.file)}
                    alt="Profile"
                    width="100%"
                    style={{ borderRadius: 8 }}
                  />
                )}
                {/* URL.createObjectURL(file) */}
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      {/* FOOTER */}
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" type="submit">
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// item size={{ xs: 12, sm: 3 }}
